import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from '@aimsos/i18n';
import {
  CatalogWidget,
  hasCatalogWidget,
  StatusMessage,
  WidgetCardShell,
  WidgetGridProvider,
  WidgetSkeleton,
} from '@aimsos/ui';

import { useWidgetData } from '../hooks/useWidgetData.js';
import { COLUMN_SPAN_BY_SIZE, PREVIEW_GRID_COLUMNS } from '../constants/widgetPreviewLayout.js';

import { ImageOff } from 'lucide-react';

/**
 * How wide the card renders at each size, as a share of the panel.
 *
 * Derived from the builder's own `COLUMN_SPAN_BY_SIZE` rather than restated,
 * so the two surfaces cannot drift: `Small` is four columns of twelve on both,
 * `Medium` eight, `Large` all twelve — which is also what the size captions
 * have always claimed ("1/3 width", "2/3 width", "Full width").
 *
 * A share, not a pixel width. The previous set — 360 / 720 / 920px — was wider
 * than the panel that holds it (585px of content), so `Medium` and `Large`
 * both clamped to 585 and rendered **identically**: past `Small`, changing the
 * size did nothing at all. A fraction cannot clamp, whatever the panel grows
 * or shrinks to.
 */
const widthPercent = (size) =>
  `${((COLUMN_SPAN_BY_SIZE[size] ?? COLUMN_SPAN_BY_SIZE.medium) / PREVIEW_GRID_COLUMNS) * 100}%`;

/** The builder preview's own easing, so a resize reads the same on both. */
const RESIZE_TRANSITION = 'transition-[width] duration-[280ms] ease-[cubic-bezier(0.16,1,0.3,1)]';

/**
 * The rendered widget inside the detail panel (CT-5188).
 *
 * Renders through `CatalogWidget` — the catalogue does not own a second set of
 * per-type renderers. Sample data comes with the registry, so a Donut here and
 * a Donut in the builder's live preview are the same drawing of the same
 * numbers.
 *
 * Left-aligned, not centred: the width change then reads as the card growing
 * rather than as it drifting sideways — the same reason the builder's preview
 * anchors to the start of its column.
 *
 * Static, unlike the builder's preview, which is a `WidgetGrid` the author can
 * drag and resize. Nothing is being authored here: this is a catalogued widget
 * being looked at, and the sizes are the same three the builder offers only so
 * the two agree about what `Medium` means.
 */
const WidgetDetailPreview = memo(function WidgetDetailPreview({ widget, size }) {
  const { t } = useTranslation('dashboards');

  const style = useMemo(() => ({ width: widthPercent(size) }), [size]);

  // The widget's own data, once it has been saved — `/widget/execute` runs the
  // stored definition, so an unsaved one has nothing to run. While it is in
  // flight the body is a skeleton; `data` null *after* that means the type has
  // no honest reading of the result, and `CatalogWidget` answers by drawing
  // its sample, which is the right thing then.
  // No `labels` here on purpose. The panel is handed a `listWidgets` row, and
  // `/widget/list` does not return `visualizationConfig` — there is no
  // definition to read a label out of without a second request. `useWidgetData`
  // names the aggregate itself, which is the one the executor cannot (CT-5579).
  const { data, hasNoData, isLoading } = useWidgetData({ widgetUuid: widget?.id });

  // A type with no renderer must say so. An empty card reads as a broken
  // widget, which is the wrong conclusion for the user to draw.
  if (!hasCatalogWidget(widget?.type)) {
    return (
      <div style={style}>
        <StatusMessage
          icon={ImageOff}
          title={t('widgetLibrary.detail.previewUnavailableTitle')}
          subtitle={t('widgetLibrary.detail.previewUnavailableSubtitle')}
        />
      </div>
    );
  }

  return (
    <div className={`h-fit overflow-hidden rounded-lg ${RESIZE_TRANSITION}`} style={style}>
      <WidgetGridProvider isEditMode={false}>
        {/* No `title`: the panel's own `SlideOutHeader` already names the widget,
            and the prototype's preview box is just the rendered body — no
            repeated title bar. `WidgetCardShell` skips its header row entirely
            when `title` is falsy, so this stays in-bounds (DS-4). */}
        <WidgetCardShell isPinned={false}>
          {isLoading ? (
            // A floor, because this card is `h-fit`: a `h-full` skeleton in a
            // container sized by its content collapses to nothing.
            <WidgetSkeleton className="min-h-[200px]" />
          ) : (
            <CatalogWidget
              type={widget.type}
              data={data ?? undefined}
              // A widget that ran draws its empty state when there is
              // nothing to draw — whether the query was empty or the
              // execution failed. The sample is for one that has not run.
              useSampleData={!hasNoData}
              compact={size === 'small'}
            />
          )}
        </WidgetCardShell>
      </WidgetGridProvider>
    </div>
  );
});

WidgetDetailPreview.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  }),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default WidgetDetailPreview;
